/**
 * `PlaylistEditorTab` — aba "Playlist" da tela /tvs/:id (TH-53378).
 *
 * Compõe AssetLibrary + PlaylistSequence + TVPreviewModal num
 * `DndContext` único. Mantém o estado local (rascunho) dos slides
 * e compara com o server-state pra decidir se há "mudanças não salvas".
 *
 * Fluxo de criação:
 *   1) GET /tvs/:id/playlist → 200 (já existe) ou 404 (vazia).
 *   2) Se 404, o único caminho de criação no MVP é o botão
 *      "Auto-gerar a partir da área" — materializa a primeira playlist
 *      e em seguida a UI permite edição manual (PUT).
 *
 * Mudanças não salvas:
 *   - Diff é feito sobre a tupla (product_art_id, custom_art_id,
 *     position, duration_ms, active) de cada slide. Ignoramos ids
 *     locais e schedule_from/until (fora do escopo desta issue —
 *     TH-53379 cobre schedule).
 *   - Botão "Salvar" desabilita quando o rascunho == server state.
 */

import { useCallback, useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Play, Save, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useCustomArtsList } from "@/hooks/useCustomArts";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";
import {
  useTVPlaylist,
  usePutTVPlaylist,
  usePlaylistAutoGen,
  type PlaylistDetailWithArts,
  type PlaylistSlideWithArt,
} from "@/hooks/useTVPlaylist";

import { AssetLibrary } from "@/components/playlist/AssetLibrary";
import {
  PlaylistSequence,
} from "@/components/playlist/PlaylistSequence";
import { TVPreviewModal } from "@/components/playlist/TVPreviewModal";
import type { DraftSlide } from "@/components/playlist/PlaylistSlideRow";

interface StoreLite {
  id: string;
  name: string;
}

interface AreaLite {
  id: string;
  name: string;
}

interface PagedStores {
  data: StoreLite[];
  meta: { total: number };
}

interface PagedAreas {
  data: AreaLite[];
  meta: { total: number };
}

async function fetchStoresAll(): Promise<PagedStores> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch("/api/stores?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedStores;
}

async function fetchAreasAll(): Promise<PagedAreas> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch("/api/areas?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedAreas;
}

export interface PlaylistEditorTabProps {
  tvId: string;
  tvName?: string;
  /** TV sem `area_id` não pode usar auto-gen (backend devolve 400). */
  tvHasArea: boolean;
  /** Operações de mutação exigem tenant_admin/super_admin. */
  canManage: boolean;
}

const DEFAULT_NEW_SLIDE_DURATION_MS = 10_000;

export function PlaylistEditorTab({
  tvId,
  tvName,
  tvHasArea,
  canManage,
}: PlaylistEditorTabProps) {
  const playlistQ = useTVPlaylist(tvId);
  const putPlaylist = usePutTVPlaylist(tvId);
  const autoGen = usePlaylistAutoGen(tvId);

  const [storeFilter, setStoreFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [draft, setDraft] = useState<DraftSlide[]>([]);
  const [serverSnapshot, setServerSnapshot] = useState<DraftSlide[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const customArtsQ = useCustomArtsList({
    store_id: storeFilter || undefined,
    area_id: areaFilter || undefined,
    active: true,
    page: 1,
    per_page: 50,
  });
  const storesQ = useQuery({
    queryKey: ["stores", "for-playlist"],
    queryFn: fetchStoresAll,
    staleTime: 5 * 60_000,
  });
  const areasQ = useQuery({
    queryKey: ["areas", "for-playlist"],
    queryFn: fetchAreasAll,
    staleTime: 5 * 60_000,
  });

  // Cache cross-filter de custom_arts (pra resolver thumb+nome em slides
  // que vieram do server mesmo quando o filtro corrente esconde a row).
  const [customArtsCache, setCustomArtsCache] = useState<
    ReadonlyMap<string, CustomArtRead>
  >(new Map());

  useEffect(() => {
    const fresh = customArtsQ.data?.data;
    if (!fresh) return;
    setCustomArtsCache((prev) => {
      const next = new Map(prev);
      for (const ca of fresh) next.set(ca.id, ca);
      return next;
    });
  }, [customArtsQ.data]);

  // Materializa o rascunho quando a query do servidor resolve. Só
  // sobrescreve o rascunho local se ele estiver igual ao snapshot atual
  // (= sem mudanças pendentes) — evita engolir edição do usuário caso a
  // query refetch enquanto ele tá editando.
  useEffect(() => {
    if (!playlistQ.data) return;
    const fresh = toDraftSlides(playlistQ.data);
    setServerSnapshot((prevSnap) => {
      setDraft((prevDraft) => {
        const draftMatchesPrevSnap = signaturesEqual(prevDraft, prevSnap);
        return draftMatchesPrevSnap ? fresh : prevDraft;
      });
      return fresh;
    });
  }, [playlistQ.data]);

  const dirty = !signaturesEqual(draft, serverSnapshot);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      // Caso 1: arrastando asset da biblioteca pra playlist.
      if (activeId.startsWith("asset:")) {
        const data = active.data.current as
          | { kind?: string; art?: CustomArtRead }
          | undefined;
        if (!data?.art || data.kind !== "custom_art") return;
        const newSlide: DraftSlide = {
          _localId: makeLocalId(),
          id: makeLocalId(), // não usado no PUT, mas mantém o shape
          playlist_id: playlistQ.data?.id ?? "",
          product_art_id: null,
          custom_art_id: data.art.id,
          position: 0, // recomputado no PUT
          duration_ms: DEFAULT_NEW_SLIDE_DURATION_MS,
          active: true,
          schedule_from: null,
          schedule_until: null,
          product_art: null,
        };
        setDraft((prev) => [...prev, newSlide]);
        return;
      }

      // Caso 2: reorder dentro da playlist (slide → slide).
      if (activeId !== overId) {
        setDraft((prev) => {
          const oldIndex = prev.findIndex((s) => s._localId === activeId);
          const newIndex = prev.findIndex((s) => s._localId === overId);
          if (oldIndex < 0 || newIndex < 0) return prev;
          return arrayMove(prev, oldIndex, newIndex);
        });
      }
    },
    [playlistQ.data?.id],
  );

  const handleDurationChange = useCallback(
    (localId: string, durationMs: number) => {
      setDraft((prev) =>
        prev.map((s) =>
          s._localId === localId ? { ...s, duration_ms: durationMs } : s,
        ),
      );
    },
    [],
  );

  const handleActiveToggle = useCallback((localId: string) => {
    setDraft((prev) =>
      prev.map((s) =>
        s._localId === localId ? { ...s, active: !s.active } : s,
      ),
    );
  }, []);

  const handleRemove = useCallback((localId: string) => {
    setDraft((prev) => prev.filter((s) => s._localId !== localId));
  }, []);

  function handleSave() {
    if (!playlistQ.data) return;
    setMutationError(null);
    putPlaylist.mutate(
      {
        slides: draft.map((s, idx) => ({
          product_art_id: s.product_art_id ?? undefined,
          custom_art_id: s.custom_art_id ?? undefined,
          position: idx + 1,
          duration_ms: s.duration_ms,
          active: s.active,
        })),
      },
      {
        onError: (err) => setMutationError(err.message),
      },
    );
  }

  function handleAutoGen() {
    setMutationError(null);
    autoGen.mutate(undefined, {
      onError: (err) => setMutationError(err.message),
    });
  }

  function handleDiscard() {
    setDraft(serverSnapshot);
    setMutationError(null);
  }

  const playlistMissing =
    playlistQ.error !== null && playlistQ.error.status === 404;
  const isLoadingFirst = playlistQ.isLoading;
  const otherError =
    playlistQ.error !== null && playlistQ.error.status !== 404
      ? playlistQ.error.message
      : null;

  if (isLoadingFirst) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Carregando playlist…
      </div>
    );
  }

  if (otherError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
      >
        Erro ao carregar playlist: {otherError}
      </div>
    );
  }

  if (playlistMissing) {
    return (
      <EmptyPlaylistState
        tvHasArea={tvHasArea}
        canManage={canManage}
        onAutoGen={handleAutoGen}
        isPending={autoGen.isPending}
        error={mutationError}
      />
    );
  }

  const customArts = customArtsQ.data?.data ?? [];
  const stores = storesQ.data?.data ?? [];
  const areas = areasQ.data?.data ?? [];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] pb-3">
        <div className="flex items-center gap-2">
          {dirty ? (
            <Badge variant="warn">• mudanças não salvas</Badge>
          ) : (
            <span className="text-xs text-[var(--color-muted-foreground)]">
              Tudo salvo.
            </span>
          )}
          {playlistQ.data ? (
            <span className="font-mono text-[10px] text-[var(--color-muted-foreground)]">
              etag {playlistQ.data.etag.slice(0, 8)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            disabled={draft.length === 0}
            title={
              draft.length === 0
                ? "Adicione pelo menos um slide pra ver o preview"
                : undefined
            }
          >
            <Play className="h-4 w-4" aria-hidden="true" />
            Visualizar como TV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAutoGen}
            disabled={!canManage || !tvHasArea || autoGen.isPending}
            title={
              !tvHasArea
                ? "Atribua uma área pra TV antes de auto-gerar"
                : undefined
            }
          >
            {autoGen.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            )}
            Auto-gerar por área
          </Button>
          {dirty ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDiscard}
              disabled={putPlaylist.isPending}
            >
              Descartar
            </Button>
          ) : null}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canManage || !dirty || putPlaylist.isPending}
          >
            {putPlaylist.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Salvar mudanças
          </Button>
        </div>
      </div>

      {mutationError ? (
        <div
          role="alert"
          className="mt-3 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
        >
          {mutationError}
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="mt-4 grid gap-4 lg:grid-cols-[300px_1fr] xl:grid-cols-[340px_1fr]">
          <AssetLibrary
            customArts={customArts}
            stores={stores}
            areas={areas}
            storeFilter={storeFilter}
            areaFilter={areaFilter}
            onStoreFilterChange={setStoreFilter}
            onAreaFilterChange={setAreaFilter}
            loading={customArtsQ.isLoading}
            error={customArtsQ.error !== null}
          />
          <PlaylistSequence
            slides={draft}
            customArtsById={customArtsCache}
            onDurationChange={handleDurationChange}
            onActiveToggle={handleActiveToggle}
            onRemove={handleRemove}
          />
        </div>
      </DndContext>

      <TVPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        slides={draft}
        customArtsById={customArtsCache}
        tvName={tvName}
      />
    </>
  );
}

function EmptyPlaylistState({
  tvHasArea,
  canManage,
  onAutoGen,
  isPending,
  error,
}: {
  tvHasArea: boolean;
  canManage: boolean;
  onAutoGen: () => void;
  isPending: boolean;
  error: string | null;
}) {
  return (
    <div className="rounded-md border border-dashed border-[var(--color-border-strong)] p-8 text-center">
      <Sparkles
        className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]"
        aria-hidden="true"
      />
      <h3 className="mt-3 text-base font-medium">
        Sem playlist ainda.
      </h3>
      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
        Comece com &ldquo;Auto-gerar a partir da área&rdquo; — o sistema
        embaralha as artes IA prontas dessa área. Depois você ajusta na mão.
      </p>
      {!tvHasArea ? (
        <p className="mt-3 text-xs text-[var(--color-warning)]">
          Atribua uma área à TV (aba Geral) antes de auto-gerar.
        </p>
      ) : null}
      <Button
        className="mt-4"
        onClick={onAutoGen}
        disabled={!canManage || !tvHasArea || isPending}
      >
        {isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        )}
        Auto-gerar por área
      </Button>
      {error ? (
        <p
          role="alert"
          className="mt-3 text-xs text-[var(--color-destructive)]"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ============== helpers ==============

function makeLocalId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `local:${crypto.randomUUID()}`;
  }
  return `local:${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function toDraftSlides(playlist: PlaylistDetailWithArts): DraftSlide[] {
  return playlist.slides.map((s: PlaylistSlideWithArt) => ({
    ...s,
    _localId: `srv:${s.id}`,
  }));
}

type SlideSignature = readonly [
  string | null, // product_art_id
  string | null, // custom_art_id
  number, // duration_ms
  boolean, // active
];

function signatureOf(slide: DraftSlide): SlideSignature {
  return [
    slide.product_art_id,
    slide.custom_art_id,
    slide.duration_ms,
    slide.active,
  ];
}

function signaturesEqual(a: DraftSlide[], b: DraftSlide[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const sa = signatureOf(a[i]);
    const sb = signatureOf(b[i]);
    for (let j = 0; j < sa.length; j++) {
      if (sa[j] !== sb[j]) return false;
    }
  }
  return true;
}
